import { Cartesian2, Cartesian3, defined, type Viewer, EasingFunction } from 'cesium';

/**
 * Smooth relative zoom toward screen center.
 *
 * @param viewer Cesium Viewer instance
 * @param relativeAmount < 1 zooms in, > 1 zooms out
 * @param duration animation duration (default 0.5s)
 */
export function relativeZoom(viewer: Viewer, relativeAmount: number, duration = 0.5) {
	if (!viewer || viewer.isDestroyed?.()) return;

	const { scene, camera } = viewer;
	if (!scene || !camera) return;

	const canvas = scene.canvas;
	const center = new Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2);

	const ray = camera.getPickRay(center);
	if (!ray) return;

	const target = scene.globe.pick(ray, scene);
	let newPosition: Cartesian3;

	if (!defined(target)) {
		// No intersection (sky) -> zoom based on camera height
		const height = camera.positionCartographic.height;

		const move = Cartesian3.multiplyByScalar(
			camera.directionWC,
			height * (1 - relativeAmount),
			new Cartesian3()
		);
		newPosition = Cartesian3.add(camera.positionWC, move, new Cartesian3());
	} else {
		// There is a target (ground/building)
		const direction = Cartesian3.subtract(target, camera.positionWC, new Cartesian3());
		const movement = Cartesian3.multiplyByScalar(direction, 1 - relativeAmount, new Cartesian3());
		newPosition = Cartesian3.add(camera.positionWC, movement, new Cartesian3());
	}

	camera.flyTo({
		destination: newPosition,
		orientation: {
			heading: camera.heading,
			pitch: camera.pitch,
			roll: camera.roll,
		},
		duration,
		easingFunction: EasingFunction.CUBIC_OUT,
	});
}
